/**
 * Footer Component
 * Site footer with contact, links, and social
 */

import React from 'react';
import { Link } from 'react-router-dom';

const Footer = () => {
  const footerLinks = {
    platform: [
      { name: 'About Us', path: '/about' },
      { name: 'Courses', path: '/courses' },
    ],
  };

  const socialLinks = [
    { name: 'LinkedIn', icon: 'linkedin', url: 'https://www.linkedin.com/in/lotusarc' },
    { name: 'YouTube', icon: 'youtube', url: 'https://www.youtube.com/@LotusArcTechnologies' },
    { name: 'Instagram', icon: 'instagram', url: 'https://www.instagram.com/lotusarctechnologies' },
  ];

  return (
    <footer id="footer" className="lh-footer bg-dark-900 text-white">
      <div className="lh-footer__container max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="lh-footer__grid grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {/* Brand Section */}
          <div className="lh-footer__brand lg:col-span-2">
            <h3 className="text-xl font-bold text-white mb-4">Lotus Arc Technologies</h3>
            <p className="lh-footer__brand-description text-dark-400 mb-6 max-w-sm">
              Empowering learners worldwide with high-quality courses taught by industry experts.
            </p>
            <div className="lh-footer__contact-info space-y-2 text-dark-400">
              <p className="lh-footer__contact-email flex items-center gap-2">
                <svg className="lh-footer__contact-icon w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                hr.info@lotusarctechnologies.com
              </p>
              <p className="lh-footer__contact-phone flex items-center gap-2">
                <svg className="lh-footer__contact-icon w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
                +27 836567199
              </p>
            </div>
          </div>

          {/* Platform Links */}
          <div className="lh-footer__links lh-footer__links--platform">
            <h4 className="lh-footer__links-title text-lg font-semibold mb-4">Platform</h4>
            <ul className="lh-footer__links-list space-y-3">
              {footerLinks.platform.map((link) => (
                <li key={link.path} className="lh-footer__links-item">
                  <Link to={link.path} className="lh-footer__link lh-footer__link--platform text-dark-400 hover:text-primary-400 transition-colors">
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

        </div>

        {/* Bottom Section */}
        <div className="lh-footer__bottom mt-12 pt-8 border-t border-dark-700 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="lh-footer__copyright text-dark-400 text-sm">
            © {new Date().getFullYear()} Lotus Arc Technologies. All rights reserved.
          </p>
          <div className="lh-footer__social flex items-center gap-4">
            {socialLinks.map((social) => (
              <a
                key={social.name}
                href={social.url}
                target="_blank"
                rel="noopener noreferrer"
                className={`lh-footer__social-link lh-footer__social-link--${social.icon} text-dark-400 hover:text-primary-400 transition-colors`}
                aria-label={social.name}
              >
                <svg className="lh-footer__social-icon w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  {social.icon === 'instagram' && (
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
                  )}
                  {social.icon === 'linkedin' && (
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                  )}
                  {social.icon === 'youtube' && (
                    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                  )}
                </svg>
              </a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
